import {
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemProps,
  ListItemSecondaryAction,
  ListItemText,
  ListProps,
  Stack,
  Tooltip
} from '@mui/material'
import { CatalogProduct, Generator } from '../types'
import { useTranslation } from 'react-i18next'
import {
  formatApperentPower,
  formatNominalCurrent,
  formatVoltage,
  roundValue
} from '../utils/string'
import { humanizeTimestamp } from '../utils/date'
import { usePolicy } from '../hooks'
import { Info, OpenInNew, Warning } from '@mui/icons-material'
import GeneratorCompanyEditButton from './GeneratorCompanyEditButton'
import { CatalogProductStatus, PowerUnit } from '../enums'
import GeneratorFieldEditButton from './GeneratorFieldEditButton'
import CatalogProductFieldEditButton from './CatalogProductFieldEditButton'
import { NavLink } from 'react-router-dom'

export interface GeneratorDetailsListProps extends Omit<ListProps, 'children'> {
  generator: Generator
  withSecondaryActions?: boolean
  withCatalogProduct?: boolean
  withDevice?: boolean
  withCompany?: boolean
  withTimestamps?: boolean
  onGeneratorChange?: (generator: Generator) => void
}

export default function GeneratorDetailsList({
  generator,
  withSecondaryActions = true,
  withCatalogProduct = true,
  withDevice = true,
  withCompany = true,
  withTimestamps = true,
  onGeneratorChange,
  ...props
}: GeneratorDetailsListProps) {
  const { t } = useTranslation('generator')

  return (
    <List {...props}>
      {/*<GeneratorCatalogProductListItem
        key="catalogProduct"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />*/}

      {withCatalogProduct && (
        <GeneratorCatalogProductNameListItem
          key="catalogProduct.name"
          generator={generator}
          withSecondaryAction={withSecondaryActions}
          onGeneratorChange={onGeneratorChange}
        />
      )}

      {withCatalogProduct && (
        <GeneratorCatalogProductManufacturerListItem
          key="catalogProduct.manufacturer"
          generator={generator}
          withSecondaryAction={withSecondaryActions}
          onGeneratorChange={onGeneratorChange}
        />
      )}

      {/*withCatalogProduct && (
        <GeneratorCatalogProductManufacturerProductCodeListItem
          key="catalogProduct.manufacturerProductCode"
          generator={generator}
          withSecondaryAction={withSecondaryActions}
          onGeneratorChange={onGeneratorChange}
        />
      )*/}

      <GeneratorNominalVoltageListItem
        key="nominalVoltage"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      <GeneratoNominalCurrentListItem
        key="nominalCurrent"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      <GeneratorApparentPowerListItem
        key="apparentPower"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      <GeneratorPowerSourceListItem
        key="powerSource"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      {/*<GeneratorSerialNumberListItem
        key="serialNumber"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />*/}

      {withCompany && (
        <GeneratorCompanyListItem
          key="company"
          generator={generator}
          withSecondaryAction={withSecondaryActions}
          onGeneratorChange={onGeneratorChange}
        />
      )}

      <GeneratorCustomerReferenceListItem
        key="customerReference"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      {withDevice && (
        <GeneratorDeviceListItem
          key="device"
          generator={generator}
          withSecondaryAction={withSecondaryActions}
          onGeneratorChange={onGeneratorChange}
        />
      )}

      <GeneratorAdditionalLoadListItem
        key="customData.additionalLoad"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      <GeneratorRelayOnThresholdListItem
        key="customData.relayOnThreshold"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      <GeneratorRelayOffThresholdListItem
        key="customData.relayOffThreshold"
        generator={generator}
        withSecondaryAction={withSecondaryActions}
        onGeneratorChange={onGeneratorChange}
      />

      {withTimestamps && (
        <ListItemButton>
          <ListItemText
            primary={humanizeTimestamp(generator.createdAt)}
            secondary={t('createdAt')}
          />
        </ListItemButton>
      )}

      {withTimestamps && (
        <ListItemButton>
          <ListItemText
            primary={humanizeTimestamp(generator.updatedAt)}
            secondary={t('updatedAt')}
          />
        </ListItemButton>
      )}
    </List>
  )
}

interface GeneratorDetailsListItemProps extends Omit<ListItemProps, 'children'> {
  generator: Generator
  withSecondaryAction?: boolean
  onGeneratorChange?: (generator: Generator) => void
}

function GeneratorCatalogProductListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator, canCreateCatalogProduct, canEditCatalogProduct } = usePolicy()

  function handleUpdate(catalogProduct: CatalogProduct) {
    onGeneratorChange?.({
      ...generator,
      catalogProduct
    })
  }

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('catalogProduct')}
        primary={generator?.catalogProduct?.name || t('noCatalogProduct')}
        primaryTypographyProps={{
          color: generator?.catalogProduct?.name ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {/*generator?.catalogProduct?.status === CatalogProductStatus.VERIFIED && (
            <CatalogProductStatusIndicator catalogProduct={generator.catalogProduct} />
          )*/}

          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              title={
                generator?.catalogProduct &&
                generator.catalogProduct.status === CatalogProductStatus.UNVERIFIED
                  ? t('enterCatalogProductDetails')
                  : t('selectCatalogProduct')
              }
              field="catalogProduct"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorCatalogProductNameListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator, canEditCatalogProduct } = usePolicy()

  function handleUpdate(catalogProduct: CatalogProduct) {
    onGeneratorChange?.({
      ...generator,
      catalogProduct
    })
  }

  if (generator?.catalogProduct === undefined || generator?.catalogProduct === null) {
    return null
  }

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('catalogProduct:name')}
        primary={generator?.catalogProduct?.name || t('catalogProduct:noName')}
        primaryTypographyProps={{
          color: generator?.catalogProduct?.name ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {generator.catalogProduct.status === CatalogProductStatus.UNVERIFIED &&
            canEditCatalogProduct(generator.catalogProduct) &&
            canEditGenerator(generator) && (
              <CatalogProductFieldEditButton
                catalogProduct={generator.catalogProduct}
                field="name"
                title={t('catalogProduct:enterName')}
                onUpdate={handleUpdate}
              />
            )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorCatalogProductManufacturerListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator, canEditCatalogProduct } = usePolicy()

  if (generator?.catalogProduct === undefined || generator?.catalogProduct === null) {
    return null
  }

  const hasManufacturer = generator.catalogProduct.manufacturer !== ''

  function handleUpdate(catalogProduct: CatalogProduct) {
    onGeneratorChange?.({
      ...generator,
      catalogProductId: catalogProduct.id,
      catalogProduct
    })
  }

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('catalogProduct:manufacturer')}
        primary={
          hasManufacturer
            ? generator.catalogProduct.manufacturer
            : t('catalogProduct:noManufacturer')
        }
        primaryTypographyProps={{
          color: hasManufacturer ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {generator?.catalogProduct &&
            generator.catalogProduct.status === CatalogProductStatus.UNVERIFIED &&
            canEditCatalogProduct(generator.catalogProduct) &&
            canEditGenerator(generator) && (
              <CatalogProductFieldEditButton
                catalogProduct={generator.catalogProduct}
                field="manufacturer"
                title={t('catalogProduct:enterManufacturer')}
                onUpdate={handleUpdate}
              />
            )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorCatalogProductManufacturerProductCodeListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator, canEditCatalogProduct } = usePolicy()

  if (generator?.catalogProduct === undefined || generator?.catalogProduct === null) {
    return null
  }

  const hasManufacturerProductCode =
    generator.catalogProduct.manufacturerProductCode !== null &&
    generator.catalogProduct.manufacturerProductCode !== ''

  function handleUpdate(catalogProduct: CatalogProduct) {
    onGeneratorChange?.({
      ...generator,
      catalogProductId: catalogProduct.id,
      catalogProduct
    })
  }

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('catalogProduct:manufacturerProductCode')}
        primary={
          hasManufacturerProductCode
            ? generator.catalogProduct.manufacturerProductCode
            : t('catalogProduct:noProductCode')
        }
        primaryTypographyProps={{
          color: hasManufacturerProductCode ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {generator?.catalogProduct &&
            generator.catalogProduct.status === CatalogProductStatus.UNVERIFIED &&
            canEditCatalogProduct(generator.catalogProduct) &&
            canEditGenerator(generator) && (
              <CatalogProductFieldEditButton
                catalogProduct={generator.catalogProduct}
                field="manufacturerProductCode"
                title={t('catalogProduct:enterManufacturerProductCode')}
                onUpdate={handleUpdate}
              />
            )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorCompanyListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { isSuperadmin, isManufacturer } = usePolicy()

  const canEditGeneratorCompany = isSuperadmin || isManufacturer

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('company')}
        primary={generator?.company?.name || t('noCompany')}
        primaryTypographyProps={{
          color: generator?.company?.name ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGeneratorCompany && (
            <GeneratorCompanyEditButton
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

interface GeneratorPowerListItemProps extends GeneratorDetailsListItemProps {
  unit?: PowerUnit
}

function GeneratorApparentPowerListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorPowerListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  const conflictsWithCatalogProduct =
    generator?.catalogProduct?.customData?.apparentPower !== undefined &&
    generator?.catalogProduct?.status === CatalogProductStatus.VERIFIED &&
    generator.apparentPower !== generator.catalogProduct.customData.apparentPower

  const canEditApparentPower = canEditGenerator(generator) && generator.nominalCurrent === null

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('apparentPower')}
        primary={`${roundValue(generator.apparentPower, 1)} kVA`}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {conflictsWithCatalogProduct && (
            <Tooltip
              title={t('apparentPowerConflictTooltip', {
                apparentPower: formatApperentPower(
                  generator?.catalogProduct?.customData?.apparentPower || null
                )
              })}
            >
              <Warning color="warning" />
            </Tooltip>
          )}

          {generator.nominalCurrent !== null && (
            <Tooltip title={t('calculatedApparentPowerTooltip')}>
              <Info color="info" sx={{ mr: 2 }} />
            </Tooltip>
          )}

          {canEditApparentPower && (
            <GeneratorFieldEditButton
              title={t('enterApparentPower')}
              field="apparentPower"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorNominalVoltageListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  const conflictsWithCatalogProduct =
    generator?.catalogProduct?.customData?.apparentPower !== undefined &&
    generator?.catalogProduct?.status === CatalogProductStatus.VERIFIED &&
    generator.nominalVoltage !== generator.catalogProduct.customData.nominalVoltage

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('nominalVoltage')}
        primary={formatVoltage(generator.nominalVoltage, true, 0)}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {conflictsWithCatalogProduct && (
            <Tooltip
              title={t('nominalVoltageConflictTooltip', {
                voltage: formatVoltage(
                  generator?.catalogProduct?.customData?.nominalVoltage || null,
                  true,
                  0
                )
              })}
            >
              <Warning color="warning" />
            </Tooltip>
          )}

          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              title={t('selectNominalVoltage')}
              field="nominalVoltage"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratoNominalCurrentListItem({
  generator,
  withSecondaryAction,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('nominalCurrent')}
        primary={
          generator.nominalCurrent
            ? formatNominalCurrent(generator.nominalCurrent, true)
            : t('noValue')
        }
        primaryTypographyProps={{
          color: generator.nominalCurrent ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              title={t('enterNominalCurrent')}
              field="nominalCurrent"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorPowerSourceListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  const conflictsWithCatalogProduct =
    generator?.catalogProduct?.customData?.apparentPower !== undefined &&
    generator?.catalogProduct?.status === CatalogProductStatus.VERIFIED &&
    generator.powerSource !== generator.catalogProduct.customData.powerSource

  return (
    <ListItemButton>
      <ListItemText secondary={t('powerSource')} primary={t(generator.powerSource)} />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {conflictsWithCatalogProduct && (
            <Tooltip
              title={t('powerSourceConflictTooltip', {
                powerSource: t(generator?.catalogProduct?.customData?.powerSource ?? '')
              })}
            >
              <Warning color="warning" />
            </Tooltip>
          )}

          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              generator={generator}
              field="powerSource"
              title={t('selectPowerSource')}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorCustomerReferenceListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  const hasCustomerReference =
    generator.customerReference !== null && generator.customerReference !== ''

  return (
    <ListItemButton>
      <ListItemText
        secondary={t('customerReference')}
        primary={hasCustomerReference ? generator.customerReference : t('noCustomerReference')}
        primaryTypographyProps={{
          color: hasCustomerReference ? 'text.primary' : 'text.disabled'
        }}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              title={t('enterCustomerReference')}
              field="customerReference"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorSerialNumberListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGenerator } = usePolicy()

  const hasSerialNumber = generator.serialNumber !== null && generator.serialNumber !== ''

  return (
    <ListItemButton>
      <ListItemText
        primary={hasSerialNumber ? generator.serialNumber : t('noSerialNumber')}
        primaryTypographyProps={{ color: hasSerialNumber ? 'text.primary' : 'text.disabled' }}
        secondary={t('serialNumber')}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGenerator(generator) && (
            <GeneratorFieldEditButton
              title={t('enterSerialNumber')}
              field="serialNumber"
              generator={generator}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorDeviceListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  return (
    <ListItemButton>
      <ListItemText
        primary={generator.device ? <>{generator.device.shortUUID}</> : <>{t('noDevice')}</>}
        primaryTypographyProps={{ color: generator?.device ? 'text.primary' : 'text.disabled' }}
        secondary={t('device')}
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {generator?.device ? (
            <Tooltip title={t('actions:show')}>
              <IconButton component={NavLink} to={`/devices/${generator?.device.name}`}>
                <OpenInNew />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              component={NavLink}
              to={`/install/generator?generatorId=${generator.id}`}
            >
              {t('installDevice')}
            </Button>
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorAdditionalLoadListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGeneratorCustomData } = usePolicy()

  const unit = generator?.customData?.additionalLoadUnit ?? PowerUnit.KILOWATT

  return (
    <ListItemButton>
      <ListItemText
        primary={
          generator?.customData?.additionalLoad !== undefined
            ? `${generator?.customData?.additionalLoad} ${unit}`
            : t('noValue')
        }
        primaryTypographyProps={{
          color:
            generator?.customData?.additionalLoad !== undefined ? 'text.primary' : 'text.disabled'
        }}
        secondary={
          <Stack direction="row" alignItems="center" gap={1}>
            {t('additionalLoad')}
            <Tooltip title={t('additionalLoadTooltip')}>
              <Info color="primary" fontSize="small" />
            </Tooltip>
          </Stack>
        }
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGeneratorCustomData(generator) && (
            <GeneratorFieldEditButton
              generator={generator}
              field="customData.additionalLoad"
              title={t('additionalLoadSettings')}
              dialogProps={{
                maxWidth: 'lg'
              }}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorRelayOnThresholdListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGeneratorCustomData } = usePolicy()

  const hasRelayOnThreshold = generator.customData?.relayOnThreshold !== undefined

  return (
    <ListItemButton key="customData.relayOffThreshold">
      <ListItemText
        primary={
          generator.customData?.relayOnThreshold !== undefined
            ? `${generator.customData?.relayOnThreshold}%`
            : t('noValue')
        }
        primaryTypographyProps={{
          color: hasRelayOnThreshold ? 'text.primary' : 'text.disabled'
        }}
        secondary={
          <Stack direction="row" alignItems="center" gap={1}>
            {t('relayOnThreshold')}
            <Tooltip title={t('relayOnThresholdTooltip')}>
              <Info color="primary" fontSize="small" />
            </Tooltip>
          </Stack>
        }
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGeneratorCustomData(generator) && (
            <GeneratorFieldEditButton
              generator={generator}
              field="customData.relayOnThreshold"
              title={t('additionalLoadSettings')}
              dialogProps={{
                maxWidth: 'lg'
              }}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}

function GeneratorRelayOffThresholdListItem({
  generator,
  withSecondaryAction = true,
  onGeneratorChange
}: GeneratorDetailsListItemProps) {
  const { t } = useTranslation('generator')

  const { canEditGeneratorCustomData } = usePolicy()

  const hasRelayOffThreshold = generator.customData?.relayOffThreshold !== undefined

  return (
    <ListItemButton>
      <ListItemText
        primary={
          generator.customData?.relayOffThreshold !== undefined
            ? `${generator.customData?.relayOffThreshold}%`
            : t('noValue')
        }
        primaryTypographyProps={{
          color: hasRelayOffThreshold ? 'text.primary' : 'text.disabled'
        }}
        secondary={
          <Stack direction="row" alignItems="center" gap={1}>
            {t('relayOffThreshold')}
            <Tooltip title={t('relayOffThresholdTooltip')}>
              <Info color="primary" fontSize="small" />
            </Tooltip>
          </Stack>
        }
      />

      {withSecondaryAction && (
        <ListItemSecondaryAction>
          {canEditGeneratorCustomData(generator) && (
            <GeneratorFieldEditButton
              generator={generator}
              field="customData.relayOffThreshold"
              title={t('additionalLoadSettings')}
              dialogProps={{
                maxWidth: 'lg'
              }}
              onGeneratorChange={onGeneratorChange}
            />
          )}
        </ListItemSecondaryAction>
      )}
    </ListItemButton>
  )
}
